import React, { useState } from 'react';
import type { User } from '@axiom/validation';
import {
  Button,
  Dropdown,
  FormLabel,
  Form,
  Gutter,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  SectionHeader,
} from '@axiom/ui';

import { UserApi } from '../../api/generated/user-api';
import { Role } from '../../enums/generated/role';

import {
  AdminModalUserManagementFormSchema,
  type AdminModalUserManagementFormValues,
} from './admin-modal-user-management-form-schema';

export type AdminModalUsersFormProps = {
  user: User | null;
};

const USER_STATUS_ACTIVE = 'Active';
const USER_STATUS_INACTIVE = 'Inactive';

const statusOptions = [
  { label: USER_STATUS_ACTIVE, value: USER_STATUS_ACTIVE },
  { label: USER_STATUS_INACTIVE, value: USER_STATUS_INACTIVE },
];

const userTypeOptions = [
  { value: Role.EnvoySales, label: 'Sales Member' },
  { value: Role.EnvoyUser, label: 'Team Member' },
  { value: Role.EnvoyAdmin, label: 'Administrator' },
];

export const AdminModalUsersForm = ({ user }: AdminModalUsersFormProps) => {
  const [shouldConfirmDeactivation, setShouldConfirmDeactivation] =
    useState(false);

  if (!user) {
    return null;
  }

  return (
    <Form
      name="USER_FORM"
      schema={AdminModalUserManagementFormSchema}
      initialValues={{
        status: user.isActive ? USER_STATUS_ACTIVE : USER_STATUS_INACTIVE,
        roles: user.roles[0] as
          AdminModalUserManagementFormValues['roles'] | undefined,
      }}
      onSubmit={async (_changed, _actions, values) => {
        await UserApi.updateUser({
          id: user.id,
          body: {
            isActive: values.status === USER_STATUS_ACTIVE,
            roles: [values.roles],
          },
        });
      }}
    >
      {({ fireSubmit, values }) => {
        const handleSave = () => {
          const isDeactivating =
            !!user.isActive && values.status === USER_STATUS_INACTIVE;
          if (isDeactivating) {
            setShouldConfirmDeactivation(true);
          } else {
            void fireSubmit();
          }
        };

        return (
          <Gutter name="USER_MANAGEMENT_FORM">
            <Gutter bottom="16px">
              <SectionHeader name="USER_NAME_HEADER">
                {user.firstName} {user.lastName}
              </SectionHeader>
            </Gutter>
            <Dropdown
              name="status"
              label="User Status"
              options={statusOptions}
              displayKey="label"
              valueKey="value"
            />
            <Gutter bottom="16px" />
            <FormLabel>Home Office</FormLabel>
            <Paragraph>No data available</Paragraph>
            <Gutter bottom="16px" />
            <Dropdown
              name="roles"
              label="User Type"
              options={userTypeOptions}
              displayKey="label"
              valueKey="value"
            />
            <Gutter bottom="16px" />
            <FormLabel>User Email</FormLabel>
            <Paragraph name="USER_EMAIL">
              {user.email || 'No data available'}
            </Paragraph>
            <Gutter top="24px">
              <Layout position="right">
                <Button name="SUBMIT_BUTTON" onClick={handleSave}>
                  Save
                </Button>
              </Layout>
            </Gutter>

            {shouldConfirmDeactivation ? (
              <Modal size="confirm">
                <ModalHeader
                  onClose={() => {
                    setShouldConfirmDeactivation(false);
                  }}
                >
                  User Status
                </ModalHeader>
                <ModalSection>
                  <Paragraph>
                    You have selected to deactivate this User. Doing so will
                    restrict their access to Envoy. Please make sure to reassign
                    any records owned by this user.
                  </Paragraph>
                </ModalSection>
                <ModalFooter>
                  <Button
                    variation="outline"
                    onClick={() => {
                      setShouldConfirmDeactivation(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    name="CONFIRM_DEACTIVATION_BUTTON"
                    onClick={() => {
                      setShouldConfirmDeactivation(false);
                      void fireSubmit();
                    }}
                  >
                    Save
                  </Button>
                </ModalFooter>
              </Modal>
            ) : null}
          </Gutter>
        );
      }}
    </Form>
  );
};
